import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import StatusBadge from "@/components/common/StatusBadge";
import DataTable from "@/components/common/DataTable";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Printer, ArrowClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const CAN_EDIT = ["admin", "owner", "admin_kantor"];
const CAN_DELETE = ["admin", "owner"];
const CAN_RESET_WEIGH = ["admin", "owner"];
const RESETTABLE_STATUS = ["weighed_in", "loading", "weighed_out"];

const PRODUCTS = ["Presol", "Minyak Bakar"];
const initial = {
  do_no: "", customer: "", customer_address: "", product: "Presol",
  truck_no: "", driver: "", target_qty: 0, schedule_date: "", notes: "",
};

export default function DeliveryPage() {
  const { user } = useAuth();
  const canEdit = CAN_EDIT.includes(user?.role);
  const canDelete = CAN_DELETE.includes(user?.role);
  const canResetWeigh = CAN_RESET_WEIGH.includes(user?.role);

  const resetWeigh = async (row) => {
    if (!window.confirm(`Reset timbang untuk DO ${row.do_no}?\n\nStatus akan kembali ke "Disetujui", data timbang akan dihapus, dan security bisa timbang ulang.\nSlip timbang lama tetap tersimpan di riwayat.`)) return;
    try {
      const { data } = await api.post(`/delivery/${row.id}/reset-weigh`);
      toast.success(data.message || `DO ${row.do_no} berhasil di-reset`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("all");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  // Task 2: ketika customer dipilih dari saran (datalist) atau diketik persis nama customer tersimpan,
  // alamat auto-fill dari data customer. Kalau user sudah edit manual alamat, tetap hormati inputnya.
  const onCustomerChange = (e) => {
    const val = e.target.value;
    setForm((f) => {
      const match = customers.find((c) => c.name.toLowerCase() === val.toLowerCase());
      // Auto-fill alamat kalau match ditemukan; skip kalau user sudah isi alamat manual yang beda
      // dari customer sebelumnya (hindari overwrite input user).
      const prevMatch = customers.find((c) => c.name.toLowerCase() === (f.customer || "").toLowerCase());
      const addressWasAutofilled = !f.customer_address || (prevMatch && f.customer_address === prevMatch.address);
      return {
        ...f,
        customer: val,
        customer_address: match && addressWasAutofilled ? (match.address || "") : f.customer_address,
      };
    });
  };

  const load = () => {
    api.get("/delivery").then((r) => setRows(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  };
  useAutoRefresh(load);

  const openNew = () => { setForm(initial); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      do_no: r.do_no, customer: r.customer, customer_address: r.customer_address || "",
      product: r.product, truck_no: r.truck_no, driver: r.driver, target_qty: r.target_qty,
      schedule_date: r.schedule_date || "", notes: r.notes || "",
    });
    setEditingId(r.id); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, target_qty: parseFloat(form.target_qty) || 0 };
      if (editingId) {
        await api.patch(`/delivery/${editingId}`, payload);
        toast.success("DO berhasil diperbarui");
      } else {
        await api.post("/delivery", payload);
        toast.success("Delivery Order dibuat & menunggu approval");
      }
      setForm(initial); setEditingId(null); setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus DO ${row.do_no}?`)) return;
    try {
      await api.delete(`/delivery/${row.id}`);
      toast.success("DO dihapus"); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const printDO = (row) => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return toast.error("Popup diblokir browser");
    w.document.write(`<!DOCTYPE html><html><head><title>${row.do_no}</title>
      <style>body{font-family:sans-serif;padding:32px;color:#0f172a}
      h1{font-size:22px;margin:0 0 6px}h2{font-size:16px;margin:20px 0 8px;border-bottom:2px solid #0ea5e9;padding-bottom:4px}
      .row{display:flex;gap:12px;margin:4px 0}.lbl{width:180px;color:#64748b;font-size:12px;text-transform:uppercase}
      .val{flex:1;font-weight:600}.big{font-size:36px;font-weight:900;color:#0ea5e9}
      .box{border:1px solid #e2e8f0;padding:16px;border-radius:8px;margin-top:12px}
      .header{display:flex;align-items:center;gap:12px;border-bottom:2px solid #0f172a;padding-bottom:12px}
      img{width:64px;height:64px;border-radius:50%}
      .approved{color:#059669;font-weight:bold}
      </style></head><body>
      <div class="header">
        <img src="/primergy-logo.jpeg" />
        <div>
          <div style="font-weight:900;font-size:18px">PT. PRIMERGY SOLUTION</div>
          <div style="font-size:12px;color:#64748b">DELIVERY ORDER</div>
        </div>
      </div>
      <h1 style="margin-top:20px">${row.do_no}</h1>
      <div class="approved">✓ Disetujui oleh ${row.approved_by_name || row.approved_by || "-"} · ${row.approved_at ? new Date(row.approved_at).toLocaleString("id-ID") : "-"}</div>

      <h2>Customer</h2>
      <div class="row"><div class="lbl">Nama</div><div class="val">${row.customer}</div></div>
      <div class="row"><div class="lbl">Alamat</div><div class="val">${row.customer_address || "—"}</div></div>

      <h2>Muatan</h2>
      <div class="row"><div class="lbl">Produk</div><div class="val">${row.product}</div></div>
      <div class="row"><div class="lbl">Target Qty</div><div class="val big">${(row.target_qty||0).toLocaleString("id-ID")} kg</div></div>

      <h2>Pengangkutan</h2>
      <div class="row"><div class="lbl">Truck</div><div class="val">${row.truck_no}</div></div>
      <div class="row"><div class="lbl">Driver</div><div class="val">${row.driver}</div></div>
      <div class="row"><div class="lbl">Jadwal Loading</div><div class="val">${row.schedule_date ? new Date(row.schedule_date).toLocaleString("id-ID") : "—"}</div></div>

      ${row.notes ? `<h2>Catatan</h2><div class="box">${row.notes}</div>` : ""}

      <div style="margin-top:60px;display:flex;justify-content:space-between">
        <div style="text-align:center"><div style="border-top:1px solid #64748b;padding-top:6px;width:200px">Pengawas Pabrik</div></div>
        <div style="text-align:center"><div style="border-top:1px solid #64748b;padding-top:6px;width:200px">Driver</div></div>
        <div style="text-align:center"><div style="border-top:1px solid #64748b;padding-top:6px;width:200px">Penerima Customer</div></div>
      </div>
      <script>window.print()</script>
      </body></html>`);
    w.document.close();
  };

  const filtered = rows.filter((r) => status === "all" || r.status === status);

  return (
    <AppShell
      title="Pengiriman (Delivery)"
      subtitle="Kelola Delivery Order — no. DO manual & customer tersimpan otomatis"
      actions={
        <Button onClick={openNew} data-testid="delivery-new-btn" className="bg-sky-600 hover:bg-sky-700 h-10 gap-2">
          <Plus size={16} weight="bold" /> <span className="hidden sm:inline">DO Baru</span>
        </Button>
      }
    >
      <div className="flex items-center gap-3 mb-5">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 h-10" data-testid="do-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="pending_approval">Menunggu Approve</SelectItem>
            <SelectItem value="approved">Disetujui</SelectItem>
            <SelectItem value="rejected">Ditolak</SelectItem>
            <SelectItem value="loading">Loading</SelectItem>
            <SelectItem value="weighed_out">Timbang Keluar</SelectItem>
            <SelectItem value="closed">Selesai</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs font-mono text-slate-500">{filtered.length} DO</span>
      </div>

      <DataTable
        testid="delivery-table"
        rows={filtered}
        columns={[
          { key: "do_no", label: "No. DO", render: (r) => <span className="font-mono font-bold text-slate-900">{r.do_no}</span> },
          { key: "customer", label: "Customer", render: (r) => (
            <div className="leading-tight">
              <div className="font-semibold">{r.customer}</div>
              {r.customer_address && <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{r.customer_address}</div>}
            </div>
          )},
          { key: "product", label: "Produk" },
          { key: "truck_no", label: "Truck", render: (r) => <span className="font-mono">{r.truck_no}</span> },
          { key: "target_qty", label: "Target (kg)", render: (r) => <span className="font-mono">{(r.target_qty || 0).toLocaleString("id-ID")}</span> },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-1">
              {r.approval_status === "approved" && (
                <button onClick={() => printDO(r)} data-testid={`do-print-${r.do_no}`}
                  className="text-emerald-700 hover:text-emerald-900 p-1.5 hover:bg-emerald-50 rounded-md" title="Print DO">
                  <Printer size={15} weight="duotone" />
                </button>
              )}
              {canResetWeigh && RESETTABLE_STATUS.includes(r.status) && (
                <button onClick={() => resetWeigh(r)} data-testid={`do-reset-weigh-${r.do_no}`}
                  className="text-orange-700 hover:text-orange-900 p-1.5 hover:bg-orange-50 rounded-md" title="Reset Timbang — kembalikan status ke Disetujui supaya security bisa timbang ulang">
                  <ArrowClockwise size={15} weight="duotone" />
                </button>
              )}
              {canEdit && (
                <button onClick={() => openEdit(r)} data-testid={`do-edit-${r.do_no}`}
                  className="text-sky-700 hover:text-sky-900 p-1.5 hover:bg-sky-50 rounded-md" title="Edit">
                  <PencilSimple size={15} weight="duotone" />
                </button>
              )}
              {canDelete && (
                <button onClick={() => remove(r)} data-testid={`do-del-${r.do_no}`}
                  className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-md" title="Hapus">
                  <Trash size={15} weight="duotone" />
                </button>
              )}
            </div>
          )},
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {editingId ? "Edit Delivery Order" : "Delivery Order Baru"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} data-testid="delivery-form" className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <Field label="No. DO (kosongkan untuk auto)">
              <Input data-testid="do-number" value={form.do_no} onChange={set("do_no")} placeholder="Ketik manual atau biarkan kosong" className="font-mono" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Customer" required>
                <Input data-testid="do-customer" value={form.customer} onChange={onCustomerChange} required list="cust-list" placeholder="Ketik atau pilih dari daftar" autoComplete="off" />
                <datalist id="cust-list">
                  {customers.map((c) => <option key={c.id} value={c.name}>{c.address}</option>)}
                </datalist>
                {customers.length > 0 && (
                  <p className="text-[10px] text-slate-500 mt-1">{customers.length} customer tersimpan. Pilih dari saran — alamat akan terisi otomatis.</p>
                )}
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Alamat Customer">
                <Textarea data-testid="do-address" rows={2} value={form.customer_address} onChange={set("customer_address")} placeholder="Terisi otomatis saat customer dipilih — bisa diedit" />
              </Field>
            </div>
            <Field label="Produk" required>
              <Select value={form.product} onValueChange={set("product")}>
                <SelectTrigger className="h-10" data-testid="do-product"><SelectValue /></SelectTrigger>
                <SelectContent>{PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Target Quantity (kg)" required>
              <Input data-testid="do-qty" type="number" step="0.01" value={form.target_qty} onChange={set("target_qty")} required className="font-mono" />
            </Field>
            <Field label="No. Plat Kendaraan" required>
              <Input data-testid="do-truck" value={form.truck_no} onChange={set("truck_no")} required className="font-mono uppercase" placeholder="Contoh: B 1234 XYZ" style={{ textTransform: "uppercase" }} />
            </Field>
            <Field label="Driver" required><Input data-testid="do-driver" value={form.driver} onChange={set("driver")} required /></Field>
            <div className="md:col-span-2">
              <Field label="Jadwal Loading"><Input data-testid="do-date" type="datetime-local" value={form.schedule_date} onChange={set("schedule_date")} /></Field>
            </div>
            <div className="md:col-span-2"><Field label="Catatan"><Textarea data-testid="do-notes" rows={2} value={form.notes} onChange={set("notes")} /></Field></div>
            <div className="md:col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="do-submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white">
                {submitting ? "Menyimpan…" : editingId ? "Perbarui DO" : "Simpan DO"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
